import { ValueAdapter } from '../adapters/value.adapter';
import { IAttribute, IViolation } from '../base/annotation';
import { CopySink } from './copysink';
import { TabularTypeDataset } from '../dataset/tabular-type.dataset';
import { ModelConstants } from '../model-constants';
import { Metamodel } from '../base/model';
import { UpdateMethod } from '../../constants/write-behavior-constants';
import { sanitizeAndFormatString } from '../../shared/utils/string.util';
import { shellStringResources } from '../../Resources/ShellStringResources';

export function getDisplayName(sink: CopySink): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const owner: any = sink.owner; // CopyActivity is not imported here to avoid circular dependency, so using 'any' type
    if (owner && Metamodel.modelOf(owner)?.name === ModelConstants.copyJobActivity && (owner.sinkDataset || owner.sourceDataset)) {
        const sourceDataset: TabularTypeDataset | undefined = owner.sourceDataset;
        const sinkDataset: TabularTypeDataset | undefined = owner.sinkDataset;
        const dataset = (sinkDataset?.schema || sinkDataset?.table || sinkDataset?.tableName) ? sinkDataset : sourceDataset;

        if (!dataset) {
            return sink.name || owner?.name || '';
        }

        const schema = dataset.schema ?? '';
        const table = dataset.table ?? dataset.tableName ?? '';

        if (!schema && !table) {
            return sink.name || owner?.name || '';
        }

        return schema && table ? `${schema}.${table}` : (table || schema);
    } else {
        return sink.name || owner?.name || '';
    }
}

export function validateUniqueKeyColumns(
    sink: CopySink,
    upsertSettingsAttr: IAttribute | undefined,
    keyColumns: string[],
    keyColumnsRequired = true,
    upsertLabel?: string
): IViolation[] {
    if (upsertLabel == null) {
        if (sink.owner != null && Metamodel.modelOf(sink.owner).name === ModelConstants.copyJobActivity) {
            upsertLabel =
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (sink.owner as any).updateMethod === UpdateMethod.SCD2 // CopyJobActivity is not imported here to avoid circular dependency, so using 'any' type
                    ? shellStringResources.WriteBehavior_Option_SCD2
                    : shellStringResources.MergeLabel;
        } else {
            upsertLabel = shellStringResources.WriteBehavior_Option_Upsert;
        }
    }
    const upsertSettings = upsertSettingsAttr != null ? ValueAdapter.toResource(sink, upsertSettingsAttr) : undefined;
    const displayName = getDisplayName(sink);
    if (upsertSettings?.type === 'Expression') {
        // parameterized upsertSettings, no keyColumns
        return [];
    } else if (keyColumnsRequired && (!keyColumns || keyColumns.length === 0)) {
        return [
            {
                context: sink,
                message: () => sanitizeAndFormatString(shellStringResources.RequiresKeyColumn, upsertLabel, displayName)
            }
        ];
    }
    const violations: IViolation[] = [];
    const uniqueNames: Set<string> = new Set();
    let hasEmptyString = false;
    keyColumns.forEach((key) => {
        if (key === '') {
            if (!hasEmptyString) {
                violations.push({
                    context: sink,
                    message: () => sanitizeAndFormatString(shellStringResources.RequiresKeyColumn, upsertLabel, displayName)
                });
                hasEmptyString = true;
            }
        } else if (uniqueNames.has(key)) {
            violations.push({
                context: sink,
                message: () => sanitizeAndFormatString(shellStringResources.DupKeyColumn, key)
            });
        } else {
            uniqueNames.add(key);
        }
    });
    return violations;
}
